import { defineStore } from "pinia";
import api from "@/modules/api";
import { getDisplayDateTime } from "@/modules/common";
import { useReply } from "@/stores/reply";
import { Moment } from "@/types/entity";

export const PER_PAGE = 6;

interface MomentMap {
  // 键为 MOMENT_TYPE
  [key: number]: {
    scrollTop: number;
    data: string[];
    init: boolean;
  };
}

export const useMoment = defineStore("moment", {
  state: () => {
    const data = {} as MomentMap;
    [0, 1, 2].forEach(
      (key) => (data[key] = { scrollTop: 0, data: [], init: false })
    );

    return {
      tabIdx: 2 as 0 | 1 | 2,
      data,
      momentMap: {} as { [key: string]: Moment },

      treeholeUuidList: [] as string[],
    };
  },
  getters: {
    newMomentList: (state): Moment[] => {
      return state.data[0].data
        .map((uuid) => state.momentMap[uuid])
        .filter((item) => !!item)
        .filter((item) => item.parentId === null);
    },
    followingMomentList: (state): Moment[] => {
      return state.data[1].data
        .map((uuid) => state.momentMap[uuid])
        .filter((item) => !!item)
        .filter((item) => item.parentId === null);
    },
    exploreMomentList: (state): Moment[] => {
      return state.data[2].data
        .map((uuid) => state.momentMap[uuid])
        .filter((item) => !!item)
        .filter((item) => item.parentId === null);
    },
    treeholeMomentList: (state): Moment[] => {
      return state.treeholeUuidList
        .map((uuid) => state.momentMap[uuid])
        .filter((item) => !!item);
    },
  },
  actions: {
    async initMomentListIfEmpty(force = false, tabIdx?: number) {
      tabIdx = tabIdx ?? this.tabIdx;
      const currentItem = this.data[tabIdx];
      if (force || (currentItem.data?.length === 0 && !currentItem.init)) {
        const moments = await this._fetchMomentList(undefined, 10, tabIdx);
        currentItem.data = moments.map((item) => item.uuid);
        currentItem.init = true;
        currentItem.scrollTop = 0;
      }
    },
    // isNext: true 为下一页，false 为上一页
    async fetchMomentList(isNext: boolean, tabIdx?: number): Promise<boolean> {
      tabIdx = tabIdx ?? this.tabIdx;
      const currentList = this.data[tabIdx].data;
      const idx = isNext ? currentList.length - 1 : 0;
      const method = isNext ? "push" : "unshift";
      if (currentList.length === 0) {
        await this.initMomentListIfEmpty(true, tabIdx);
        return this.data[tabIdx].data.length !== 0;
      } else {
        const cursor = this.useCursorEncode(
          this.momentMap[currentList[idx]].dtLastActive!,
          isNext
        );
        const moments = await this._fetchMomentList(cursor);
        if (moments.length === 0) return false;
        currentList[method](...moments.map((item) => item.uuid));
        return moments.length >= PER_PAGE;
      }
    },

    async _fetchMomentList(
      cursor?: string,
      pageSize = PER_PAGE,
      _idx?: number
    ): Promise<Moment[]> {
      let data: Moment[] = [];
      const idx = _idx ?? this.tabIdx;
      if (idx === 0) {
        const res = await api.readMomentFriendsPage({
          pagination: { perPage: pageSize },
          cursor,
          filter: { filter: 3 },
        });
        data = res.page.data;
      } else if (idx === 1) {
        const res = await api.readMomentFriendsPage({
          pagination: { perPage: pageSize },
          cursor,
          filter: { filter: 2 },
        });
        data = res.page.data;
      } else {
        const res = await api.readMomentExplorePage({
          pagination: { perPage: pageSize },
          cursor,
        });
        data = res.page.data;
      }

      // else if (idx === 1) {
      //   const res = await api.readMomentFollowingPage({
      //     pagination: { perPage: pageSize },
      //     cursor,
      //   });
      //   data = res.page.data;
      // }
      this.updateMomentMap(data);
      return data;
    },

    async initTreeholeMomentListIfEmpty() {
      const res = await api.readTreeholeMomentPage({
        filter: { status: 1 },
        pagination: { perPage: PER_PAGE },
      });
      this.updateMomentMap(res.page.data);
      this.treeholeUuidList = res.page.data.map((item) => item.uuid);
    },

    async fetchTreeholeMomentList(cursor?: string, pageSize = PER_PAGE) {
      if (!cursor && this.treeholeUuidList.length > 0) {
        cursor = this.useCursorEncode(
          this.momentMap[
            this.treeholeUuidList[this.treeholeUuidList.length - 1]
          ].dtCreate!,
          true
        );
      }
      const res = await api.readTreeholeMomentPage({
        filter: { status: 1 },
        pagination: { perPage: pageSize },
        cursor,
      });
      const data = res.page.data;
      this.updateMomentMap(data);
      this.treeholeUuidList.push(...data.map((item) => item.uuid));
      return data.length === PER_PAGE;
    },

    async updateTreeholeMomentList() {
      const res = await api.readTreeholeMomentPage({
        filter: { status: 1 },
        pagination: { perPage: PER_PAGE },
      });
      this.updateMomentMap(res.page.data);
    },

    resetFollowingList() {
      this.data[1].data = [];
      this.data[1].init = false;
      this.fetchMomentList(true, 1);
    },

    async fetchMomentByMateId(
      mateId: number,
      cursor?: string,
      keyword?: string,
      excludeCollectionId?: number,
      sort?: { field: string; order: "asc" | "desc" }
    ) {
      const res = await api.readMomentPage({
        filter: { mateId, keyword, excludeCollectionId },
        pagination: { perPage: PER_PAGE },
        cursor,
        sort,
      });
      const data = res.page.data;
      this.updateMomentMap(data);
      return data;
    },
    async fetchMomentByRootId(mateId: number, rootId: number, perPage = 999) {
      const res = await api.readMomentPage({
        filter: { mateId, rootId },
        pagination: { perPage },
        sort: { field: "id", order: "asc" },
      });
      const data = res.page.data;
      this.updateMomentMap(data);
      return data;
    },
    async getOrFetchMomentItem(
      uuid: string,
      force = false
    ): Promise<Moment | undefined> {
      const item = this.momentMap[uuid];
      if (!force && item) {
        return item;
      } else {
        const { item } = await api.readOneMoment(uuid);
        this.updateMomentMap([item]);
        return item;
      }
    },
    updateMomentMap(moments: Moment[]) {
      // TODO 用watch解耦
      moments.forEach((moment) => {
        this.momentMap[moment.uuid] = moment;
        this.momentMap[moment.id!] = moment;
      });
      const replyStore = useReply();
      replyStore.updateMap(
        moments.filter((item) => item.reply).map((item) => item.reply)
      );
      moments.forEach((moment) => {
        if (moment.reply && replyStore.getReply(moment.reply.id!)) {
          moment.reply = replyStore.getReply(moment.reply.id!)!;
        }
      });
    },
    async deleteMoment(uuid: string) {
      await api.deleteOneMoment(uuid);
      Object.values(this.data).forEach((item) => {
        item.data = item.data.filter((id: string) => id !== uuid);
      });
      delete this.momentMap[uuid];
    },
    useCursorEncode(dtLastActive: string, pointsToNextItems = false): string {
      return btoa(
        JSON.stringify({
          dtLastActive: getDisplayDateTime(dtLastActive, true, true),
          _pointsToNextItems: pointsToNextItems,
        })
      );
    },
    useCursorEncodeByCreate(
      dtCreate: string,
      pointsToNextItems = false
    ): string {
      return btoa(
        JSON.stringify({
          dtCreate: getDisplayDateTime(dtCreate, true, true),
          _pointsToNextItems: pointsToNextItems,
        })
      );
    },

    async getDraftMoments(mateId: number): Promise<Moment[]> {
      const res = await api.readMomentPage({
        filter: { mateId, status: "draft" },
        pagination: { perPage: 999 },
      });
      return res.page.data;
    },
    async deleteDraftMoment(uuid: string[]) {
      for (const id of uuid) {
        await api.deleteOneMoment(id);
      }
    },
  },
});
